// ═══════════════════════════════════════════════════════════════════════════
// PAGE: STATISTIQUES D'USAGE (tenant-wide)
// Migration S5.3 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// En tête : usage des fonctions IA (consommation du mois vs crédits, et
// historique mois/mois ventilé Analyse/Rédaction). Puis vue agrégée du
// tenant : projets/étape, actions/projet, risques/projet, users RACI, post-it.

interface UsageStatItem {
  label: string;
  count: number;
}

interface UsageStatsData {
  total_postits?: number;
  projects_by_stage?: UsageStatItem[];
  actions_by_project?: UsageStatItem[];
  risks_by_project?: UsageStatItem[];
  users_by_project?: UsageStatItem[];
}

interface IaUsagePeriod {
  period_start: string;
  label: string;
  analyze: number;
  redaction: number;
  review: number;
}

interface IaUsageData {
  ia_credits: number;
  current: IaUsagePeriod & { total: number };
  history: IaUsagePeriod[];
}

declare global {
  interface Window {
    _renderStatsUsageCharts: typeof _renderStatsUsageCharts;
    _renderHistogram: typeof _renderHistogram;
    _renderIaUsageCharts: typeof _renderIaUsageCharts;
  }
}

pages.statsUsage = async function () {
  setHeader(t('pageStatsUsage.title'), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);

  const [usageRes, iaRes] = await Promise.allSettled([api.getUsageStats(), api.getIaUsage()]);

  if (usageRes.status !== 'fulfilled') {
    const e = usageRes.reason;
    const msg = e instanceof Error ? e.message : t('pageStatsUsage.errLoad');
    setContent(`<div class="error" style="padding:16px">${esc(msg)}</div>`);
    return;
  }
  const data = usageRes.value as UsageStatsData;
  // L'usage IA partage la perm statistics_usage.read ; si l'appel échoue
  // malgré tout, on dégrade en masquant juste les 2 cartes IA.
  const iaData = iaRes.status === 'fulfilled' ? (iaRes.value as IaUsageData) : null;

  const iaCards = iaData
    ? `
      <div class="card" style="padding:12px;grid-column:1 / span 2">
        <h3 style="margin:0 0 8px 0;font-size:13px">${esc(t('pageStatsUsage.cardIaMonth'))}</h3>
        <div id="stats-ia-month-caption" style="font-size:12px;color:var(--text3);margin-bottom:6px"></div>
        <div id="stats-ia-month" style="height:130px"></div>
      </div>

      <div class="card" style="padding:12px;grid-column:1 / span 2">
        <h3 style="margin:0 0 8px 0;font-size:13px">${esc(t('pageStatsUsage.cardIaHistory'))}</h3>
        <div id="stats-ia-history" style="height:300px"></div>
      </div>`
    : '';

  setContent(`
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;padding:8px">

      ${iaCards}

      <div class="card" style="padding:12px">
        <h3 style="margin:0 0 8px 0;font-size:13px">${esc(t('pageStatsUsage.cardProjectsByStage'))}</h3>
        <div id="stats-projects-by-stage" style="height:280px"></div>
      </div>

      <div class="card" style="padding:12px;display:flex;flex-direction:column;align-items:center;justify-content:center">
        <div style="font-size:11px;color:var(--text3);margin-bottom:6px">${esc(t('pageStatsUsage.cardTotalPostits'))}</div>
        <div style="font-size:48px;font-weight:600">${data.total_postits || 0}</div>
      </div>

      <div class="card" style="padding:12px;grid-column:1 / span 2">
        <h3 style="margin:0 0 8px 0;font-size:13px">${esc(t('pageStatsUsage.cardActionsByProject'))}</h3>
        <div id="stats-actions-by-project" style="height:300px"></div>
      </div>

      <div class="card" style="padding:12px;grid-column:1 / span 2">
        <h3 style="margin:0 0 8px 0;font-size:13px">${esc(t('pageStatsUsage.cardRisksByProject'))}</h3>
        <div id="stats-risks-by-project" style="height:300px"></div>
      </div>

      <div class="card" style="padding:12px;grid-column:1 / span 2">
        <h3 style="margin:0 0 8px 0;font-size:13px">${esc(t('pageStatsUsage.cardUsersByProject'))}</h3>
        <div id="stats-users-by-project" style="height:300px"></div>
      </div>

    </div>
  `);

  if (iaData) _renderIaUsageCharts(iaData);
  _renderStatsUsageCharts(data);
};

interface ChartCtx {
  chart: { data: { labels: string[] } };
  dataIndex: number;
}

// Couleurs fixes des 2 fonctions IA — stables entre les 2 graphes.
const IA_COLOR_ANALYZE = '#3E7CB1';
const IA_COLOR_REDACTION = '#E0913A';
const IA_COLOR_REVIEW = '#8B5CF6';

// Les séries IA, déclarées UNE fois et lues par les deux graphes. `review`
// (revue du référentiel, août 2026) manquait aux deux : ses crédits étaient
// décomptés du solde sans jamais apparaître dans la consommation affichée.
const IA_SERIES: ReadonlyArray<{
  key: 'analyze' | 'redaction' | 'review';
  labelKey: string;
  color: string;
}> = [
  { key: 'analyze', labelKey: 'pageStatsUsage.iaSeriesAnalyze', color: IA_COLOR_ANALYZE },
  { key: 'redaction', labelKey: 'pageStatsUsage.iaSeriesRedaction', color: IA_COLOR_REDACTION },
  { key: 'review', labelKey: 'pageStatsUsage.iaSeriesReview', color: IA_COLOR_REVIEW },
];

function _renderIaUsageCharts(ia: IaUsageData): void {
  const cur = ia.current;
  const credits = Number(ia.ia_credits) || 0;
  const total = IA_SERIES.reduce((n, s) => n + (cur[s.key] || 0), 0);

  // ── Carte 1 : consommation du mois courant vs plafond de crédits ──
  const caption = document.getElementById('stats-ia-month-caption');
  if (caption) {
    caption.textContent =
      credits > 0
        ? t('pageStatsUsage.iaMonthCaption', { used: total, credits })
        : t('pageStatsUsage.iaMonthCaptionNoCredits', { used: total });
  }
  const monthEl = document.getElementById('stats-ia-month');
  if (monthEl) {
    // Ligne pointillée au niveau du plafond de crédits — rend le « vs
    // crédits » explicite sur la barre empilée.
    const creditLinePlugin = {
      id: 'iaCreditLine',
      afterDatasetsDraw(chart: {
        ctx: CanvasRenderingContext2D;
        chartArea: { top: number; bottom: number } | null;
        scales: { x?: { getPixelForValue(v: number): number } };
      }) {
        if (credits <= 0) return;
        const xScale = chart.scales.x;
        if (!xScale || !chart.chartArea) return;
        const x = xScale.getPixelForValue(credits);
        const { ctx, chartArea } = chart;
        ctx.save();
        ctx.beginPath();
        ctx.setLineDash([5, 4]);
        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#C0392B';
        ctx.stroke();
        ctx.restore();
      },
    };
    makeChart(monthEl, {
      type: 'bar',
      data: {
        labels: [cur.label],
        datasets: IA_SERIES.map((s) => ({
          label: t(s.labelKey),
          data: [cur[s.key] || 0],
          backgroundColor: s.color,
        })),
      },
      options: {
        ...chartCommonOptions(),
        indexAxis: 'y',
        plugins: {
          legend: { display: true, position: 'bottom' },
          datalabels: {
            color: '#fff',
            font: { weight: 'bold', size: 11 },
            formatter: (v: number) => (v > 0 ? v : ''),
          },
        },
        scales: {
          x: {
            stacked: true,
            beginAtZero: true,
            suggestedMax: Math.max(credits, total, 1),
            ticks: { precision: 0 },
          },
          y: { stacked: true },
        },
      },
      plugins: [
        creditLinePlugin,
        ...(typeof ChartDataLabels !== 'undefined' ? [ChartDataLabels] : []),
      ],
    });
  }

  // ── Carte 2 : historique mois/mois, barres empilées Analyse/Rédaction ──
  const histEl = document.getElementById('stats-ia-history');
  if (histEl) {
    const hist = ia.history || [];
    if (!hist.length) {
      histEl.innerHTML = `<div style="color:var(--text3);font-size:12px;padding:8px">${esc(t('pageStatsUsage.noData'))}</div>`;
    } else {
      makeChart(histEl, {
        type: 'bar',
        data: {
          labels: hist.map((h) => h.label),
          datasets: IA_SERIES.map((s) => ({
            label: t(s.labelKey),
            data: hist.map((h) => h[s.key] || 0),
            backgroundColor: s.color,
          })),
        },
        options: {
          ...chartCommonOptions(),
          plugins: {
            legend: { display: true, position: 'bottom' },
            datalabels: {
              color: '#fff',
              font: { weight: 'bold', size: 10 },
              formatter: (v: number) => (v > 0 ? v : ''),
            },
          },
          scales: {
            x: { stacked: true, ticks: { font: { size: 10 }, maxRotation: 45, minRotation: 30 } },
            y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
          },
        },
        plugins: typeof ChartDataLabels !== 'undefined' ? [ChartDataLabels] : [],
      });
    }
  }
}

function _renderStatsUsageCharts(data: UsageStatsData): void {
  const stages = data.projects_by_stage || [];
  if (stages.length) {
    const total = stages.reduce((s, it) => s + (it.count || 0), 0);
    const centerTextPlugin = {
      id: 'centerText_projects_by_stage',
      afterDatasetDraw(chart: {
        ctx: CanvasRenderingContext2D;
        chartArea: { left: number; right: number; top: number; bottom: number } | null;
      }) {
        const { ctx, chartArea } = chart;
        if (!chartArea) return;
        const cx = (chartArea.left + chartArea.right) / 2;
        const cy = (chartArea.top + chartArea.bottom) / 2;
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#1A2540';
        ctx.font = 'bold 28px Inter, system-ui, sans-serif';
        ctx.fillText(String(total), cx, cy - 4);
        ctx.font = '11px Inter, system-ui, sans-serif';
        ctx.fillStyle = '#646E7E';
        ctx.fillText(t('pageStatsUsage.donutCenter'), cx, cy + 14);
        ctx.restore();
      },
    };
    const container = document.getElementById('stats-projects-by-stage');
    if (container) {
      makeChart(container, {
        type: 'doughnut',
        data: {
          labels: stages.map((s) => s.label),
          datasets: [
            { data: stages.map((s) => s.count), backgroundColor: chartColors(stages.length) },
          ],
        },
        options: {
          ...chartCommonOptions(),
          cutout: '62%',
          plugins: {
            ...(chartCommonOptions() as { plugins?: Record<string, unknown> }).plugins,
            datalabels: {
              color: '#fff',
              font: { weight: 'bold', size: 11 },
              formatter: (value: number, ctx: ChartCtx) => {
                if (!value) return '';
                const label = ctx.chart.data.labels[ctx.dataIndex];
                return `${label}: ${value}`;
              },
            },
          },
        },
        plugins: [
          centerTextPlugin,
          ...(typeof ChartDataLabels !== 'undefined' ? [ChartDataLabels] : []),
        ],
      });
    }
  }

  _renderHistogram(
    'stats-actions-by-project',
    data.actions_by_project,
    t('pageStatsUsage.dsActions'),
  );
  _renderHistogram('stats-risks-by-project', data.risks_by_project, t('pageStatsUsage.dsRisks'));
  _renderHistogram('stats-users-by-project', data.users_by_project, t('pageStatsUsage.dsUsers'));
}

function _renderHistogram(
  containerId: string,
  items: ReadonlyArray<UsageStatItem> | undefined,
  datasetLabel: string,
): void {
  const el = document.getElementById(containerId);
  if (!el) return;
  if (!items || !items.length) {
    el.innerHTML = `<div style="color:var(--text3);font-size:12px;padding:8px">${esc(t('pageStatsUsage.noData'))}</div>`;
    return;
  }
  makeChart(el, {
    type: 'bar',
    data: {
      labels: items.map((i) => i.label),
      datasets: [
        {
          label: datasetLabel,
          data: items.map((i) => i.count),
          backgroundColor: chartColors(items.length),
        },
      ],
    },
    options: {
      ...chartCommonOptions(),
      plugins: {
        legend: { display: false },
        datalabels: {
          anchor: 'end',
          align: 'top',
          color: 'var(--text2)',
          font: { weight: 'bold', size: 10 },
          formatter: (v: number) => (v > 0 ? v : ''),
        },
      },
      scales: {
        x: { ticks: { font: { size: 10 }, maxRotation: 45, minRotation: 30 } },
        y: { beginAtZero: true, ticks: { precision: 0 } },
      },
    },
    plugins: typeof ChartDataLabels !== 'undefined' ? [ChartDataLabels] : [],
  });
}

// ── ESM globals shim (lot B.8 + S5.3) ──
window._renderStatsUsageCharts = _renderStatsUsageCharts;
window._renderHistogram = _renderHistogram;
window._renderIaUsageCharts = _renderIaUsageCharts;

export {};
